import React, { useState } from 'react';
import { Box, Tabs, Tab, Paper, Typography } from '@mui/material';
import { ROLES } from '../../constants';
import { getUserDetails } from '../../utils';
import { TAB_NAME_TO_VALUE, BASE_TABS } from './constants';
import OverviewTab from './tabs/OverviewTab';
import EngagementTab from './tabs/EngagementTab';
import MeetingsTab from './tabs/MeetingsTab';
import SurveysTab from './tabs/SurveysTab';
import PairingTab from './tabs/PairingTab';
import GoalsTab from './tabs/GoalsTab';
import DemographicsTab from './tabs/DemographicsTab';
import SchoolsTab from './tabs/SchoolsTab';
import TrendsTab from './tabs/TrendsTab';

// ----------------------------------------------------------------------

export default function AnalyticsDashboard({ data, isLoading }) {

  const userDetails = getUserDetails();
  const role = userDetails?.role
  const [analyticsTab, setAnalyticsTab] = useState(0);
  const availableTabs = role === ROLES.ADMIN 
    ? [...BASE_TABS, 'Schools', 'Trends']
    : [...BASE_TABS, 'Trends'];
  const analyticsData = data?.data?.data || {};
  const {
    overview,
    engagement,
    meetings,
    surveys,
    pairing,
    goals,
    demographics,
    schools,
    trends,
    MsCs,
  } = analyticsData;

  const handleAnalyticsTabChange = (event, newValue) => {
    const tabName = event?.target?.textContent?.trim();
    if (tabName && typeof TAB_NAME_TO_VALUE[tabName] === 'number') {
      setAnalyticsTab(TAB_NAME_TO_VALUE[tabName]);
      return;
    }
    setAnalyticsTab(newValue);
  };

  const renderTabContent = () => {
    switch (analyticsTab) {
      case TAB_NAME_TO_VALUE.Overview:
        return <OverviewTab data={overview} msCsData={MsCs} />;
      case TAB_NAME_TO_VALUE.Engagement:
        return <EngagementTab data={engagement} />;
      case TAB_NAME_TO_VALUE.Sessions:
        return <MeetingsTab data={meetings} />;
      case TAB_NAME_TO_VALUE.Surveys:
        return <SurveysTab data={surveys} />;
      case TAB_NAME_TO_VALUE.Pairing:
        return <PairingTab data={pairing} />;
      case TAB_NAME_TO_VALUE.Goals:
        return <GoalsTab data={goals} />;
      case TAB_NAME_TO_VALUE.Demographics:
        return <DemographicsTab data={demographics} />;
      case TAB_NAME_TO_VALUE.Schools:
        return role === ROLES.ADMIN ? <SchoolsTab data={schools} /> : null;
      case TAB_NAME_TO_VALUE.Trends:
        return <TrendsTab data={trends} />;
      default:
        return null;
    }
  };

  // Don't show loading here - parent component already handles it
  if (!data?.data?.data) {
    return (
      <Box display="flex" justifyContent="center" py={4}>
        <Typography variant="h6" color="text.secondary">
          Loading
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 2 }}>
      <Paper sx={{ mb: 3, boxShadow: 2 }}>
        <Tabs 
          value={analyticsTab} 
          onChange={handleAnalyticsTabChange}
          variant="scrollable"
          scrollButtons="auto"
          sx={{
            '& .MuiTab-root': {
              minWidth: 'auto',
              padding: '12px 16px',
              fontSize: '0.875rem',
              fontWeight: 500,
              color: 'text.secondary',
              '&.Mui-selected': {
                color: 'primary.main',
                fontWeight: 600,
              },
              '&:hover': {
                color: 'primary.main',
              }
            },
            '& .MuiTabs-indicator': {
              backgroundColor: 'primary.main',
              height: 3,
            }
          }}
        >
          {availableTabs.map((tabLabel) => (
            <Tab 
              key={tabLabel} 
              label={tabLabel} 
              value={TAB_NAME_TO_VALUE[tabLabel]}
            />
          ))}
        </Tabs>
      </Paper>

      {renderTabContent()}
    </Box>
  );
}
